import { createContext, useContext } from "react";
import type { ServerProvisioningMode } from "@/hooks/useServerProvisioningGate";

// ─── Context value type ───────────────────────────────────────────────────────

/**
 * Server provisioning access state injected by `ServerProvisioningGate`
 * and consumed by child components via `useServerProvisioningContext()`.
 *
 * Components inside the gate tree read this instead of making additional
 * Convex queries, keeping the request count to one per tree.
 */
export type ServerProvisioningContextValue = {
  /**
   * Current access mode:
   * - "full"            — Active paid subscription; all actions enabled.
   * - "trial_view_only" — Free trial; view-only, actions disabled.
   *
   * "expired" and "no_access" modes never reach children (the gate redirects).
   */
  mode: Extract<ServerProvisioningMode, "full" | "trial_view_only">;
  /** Whether action buttons (provision, manage, config) should be disabled */
  isViewOnly: boolean;
  /** Max server slots for this user */
  serverSlotLimit: number;
  /** Days remaining on free trial. null when not on a free trial. */
  trialDaysRemaining: number | null;
  /** When the free trial ends (ms epoch). null when not on a free trial. */
  trialEndsAt: number | null;
  /** True when the user is on a self-service free trial (no Polar billing ID) */
  isFreeTrial: boolean;
  /** Current subscription tier */
  tier: string;
  /** Current billing status */
  status: string;
};

// ─── Context ──────────────────────────────────────────────────────────────────

export const ServerProvisioningContext =
  createContext<ServerProvisioningContextValue | null>(null);

// ─── Consumer hook ────────────────────────────────────────────────────────────

/**
 * useServerProvisioningContext
 *
 * Returns the server provisioning access state injected by the nearest
 * `ServerProvisioningGate` ancestor. Throws if called outside such a tree.
 *
 * @example
 * ```tsx
 * function ProvisionButton() {
 *   const { isViewOnly, trialDaysRemaining } = useServerProvisioningContext();
 *   return (
 *     <button disabled={isViewOnly}>
 *       {isViewOnly ? "Upgrade to Provision" : "Provision Server"}
 *     </button>
 *   );
 * }
 * ```
 */
export function useServerProvisioningContext(): ServerProvisioningContextValue {
  const ctx = useContext(ServerProvisioningContext);
  if (!ctx) {
    throw new Error(
      "useServerProvisioningContext must be called inside a <ServerProvisioningGate> tree."
    );
  }
  return ctx;
}
